import {
  convertExpiresInToUtcTime,
  getCookieValue,
} from "@mediavine/shared-ui";
import {
  REACT_APP_BACKEND_BASE_URL,
  REACT_APP_HAS_LOGGED_IN_COOKIE_NAME,
  GROWME_ANONYMOUS_ID_COOKIE_NAME,
} from "./constants";

function getIsIframe() {
  return window.parent.location !== window.location;
}

function generateAnonymousId() {
  const url = URL.createObjectURL(new Blob());
  return url.substring(url.lastIndexOf("/") + 1);
}

interface AutoLoginResponse {
  token_type: "Bearer";
  access_token: string;
  refresh_token: string;
  expires_in: number;
}

// Return value means that auto login was successful
async function autoLogin(): Promise<boolean> {
  const isIframe = getIsIframe();
  const targetWindow = window.opener || (isIframe ? window.parent : null);
  const searchParams = new URLSearchParams(window.location.search);
  const callbackUrl = searchParams.get("callback") || "";
  const siteId = searchParams.get("siteId");
  const notLoggedIn =
    getCookieValue(REACT_APP_HAS_LOGGED_IN_COOKIE_NAME) !== "true";
  let targetOrigin = "";
  let isAllowedDomain = false;

  // If the user isn't logged in but we aren't in an iframe, or if there isn't window to target or siteId information
  if (!targetWindow || !siteId || (notLoggedIn && !isIframe)) {
    return false;
  }

  // When autologin fails in an iframe, use anonymous id flow
  if (notLoggedIn) {
    try {
      const checkForAnonymousId = () => {
        const existingAnonymousId = getCookieValue(
          GROWME_ANONYMOUS_ID_COOKIE_NAME
        );
        if (!existingAnonymousId) {
          return false;
        }
        targetWindow.postMessage(
          {
            type: "MV_ANONYMOUS_ID_FOUND",
            anonymousId: existingAnonymousId,
          },
          targetOrigin || "*"
        );
        return true;
      };

      if (!checkForAnonymousId()) {
        const cookiePrefix = "growme_anonymous_id=";
        const cookieId = generateAnonymousId();
        const cookieParams = "SameSite=None; Secure;";
        let date = new Date();
        date.setTime(date.getTime() + 157784630000);
        const cookieExpiration = "expires=" + date.toUTCString();
        document.cookie = `${cookiePrefix}${cookieId}; ${cookieParams} ${cookieExpiration}`;
        if (!checkForAnonymousId()) {
          targetWindow.postMessage(
            {
              type: "MV_COOKIES_NOT_ALLOWED",
            },
            targetOrigin || "*"
          );

          return false;
        }
      }
      return true;
    } catch (e) {
      if (process.env.NODE_ENV === "development") {
        console.error(e);
      }
      return false;
    }
  }

  try {
    targetOrigin = new URL(callbackUrl || "").origin;
    let domain = new URL(targetOrigin).hostname;
    domain = domain.startsWith("www.") ? domain.replace("www.", "") : domain;
    const isAllowedResp = await fetch(`${REACT_APP_BACKEND_BASE_URL}/graphql`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        query: `
              query GetIsAllowedDomain($domain: String!) {
                isAllowedDomain(domain: $domain) {
                  isAllowed
                }
              }
            `,
        variables: { domain },
        operationName: "GetIsAllowedDomain",
      }),
    });
    const IsAllowedBody = await isAllowedResp.json();
    isAllowedDomain = Boolean(IsAllowedBody?.isAllowedDomain.isAllowed);
  } catch (e) {}

  if (callbackUrl && !isAllowedDomain) {
    // TODO: Send snowplow event
  }

  const resp = await fetch(`${REACT_APP_BACKEND_BASE_URL}/auto-login`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify({
      siteId,
    }),
  });

  const body: AutoLoginResponse | { error: string } = await resp.json();

  if ("error" in body) {
    return false;
  }

  targetWindow.postMessage(
    {
      type: "MV_AUTHENTICATED",
      accessToken: body.access_token,
      refreshToken: body.refresh_token,
      expirationTime: convertExpiresInToUtcTime(body.expires_in),
      authType: "login",
      isAutoLogin: true,
      isIframe,
    },
    targetOrigin || "*"
  );
  window.close();
  return true;
}

async function renderApp() {
  if (window.location.pathname !== "/logout") {
    let isSuccessful = false;
    try {
      isSuccessful = await autoLogin();
    } catch (e) {}
    if (isSuccessful || getIsIframe()) {
      return;
    }
  }

  const betaName = document.currentScript?.getAttribute(
    "data-grow-me-beta-name"
  );
  const betaVersion = document.currentScript?.getAttribute(
    "data-grow-me-beta-version"
  );

  // Avoiding async await here so we don't have to include regenerator runtime in main.js.
  // Could change to async await if browserlist settings were adjusted to only include browsers
  // that natively support it. We probably can adjust the browserlist config since we need support
  // for shadow DOM anyways, but not messing with it right now.
  import("./initializeApp").then(({ initializeApp }) => {
    initializeApp({ betaName, betaVersion });
  });
}

renderApp();

export {};
